import { useEffect, useRef, useState } from 'react';
import PosLandingTick from './PosLandingTick';
import PosPasswordPeekIcon from './PosPasswordPeekIcon';
import {
  LOGIN_EMAIL_PLACEHOLDER, LOGIN_EMAIL_ERROR, LOGIN_PASSWORD_ERROR,
  LOGIN_CAPS_LOCK_WARNING, LOGIN_EMAIL_PATTERN,
} from '../constants/posLoginConst';

/**
 * The email + password form every sign-in screen uses (temp/login.html).
 *
 * It owns everything that is about TYPING — the field values, the inline
 * checks, the caps-lock warning, show/hide, the busy state — and nothing that
 * is about WHO may sign in. That decision belongs to the screen, which gets
 * the cleaned-up values through `onSubmit` and reports a refusal back through
 * `error`. Keeping the access rules out of here is what lets the till and
 * /admin share one form while checking completely different things.
 *
 * The password is cleared after every attempt that comes back, because each
 * refusal path in the screens used to clear it by hand; a successful one
 * unmounts the form anyway.
 *
 * @param {object} props
 * @param {(email: string, password: string, remember: boolean) => Promise<void>} props.onSubmit
 *   - called only once both fields pass the inline checks
 * @param {string} [props.error] - why the last attempt was refused; '' for none
 * @param {string} [props.forgotMessage] - shown when "Forgot password?" is pressed; omit to hide the link
 * @param {boolean} [props.showRemember] - offer "Keep me signed in"
 * @returns {JSX.Element}
 */
function PosLoginForm({
  onSubmit, error = '', forgotMessage = '', showRemember = true,
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [passwordMissing, setPasswordMissing] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [busy, setBusy] = useState(false);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  // Guards the post-await state updates: a successful sign-in unmounts this
  // form before onSubmit's promise settles.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  /**
   * Caps Lock is the commonest cause of a refused password, and the browser
   * only reports it on key events — so both keydown and keyup ask.
   * @param {React.KeyboardEvent<HTMLInputElement>} event
   */
  const checkCapsLock = (event) => {
    setCapsLock(Boolean(event.getModifierState?.('CapsLock')));
  };

  /** Shows or hides the password, keeping the cursor in the field. */
  const togglePassword = () => {
    setShowPassword((shown) => !shown);
    passwordRef.current?.focus();
  };

  /**
   * Checks the fields, then hands them to the screen. Nothing reaches
   * Firebase unless both pass, so an empty or malformed address is answered
   * at the field rather than with a generic error after a round trip.
   * @param {React.FormEvent<HTMLFormElement>} event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setShowForgot(false);

    const cleanEmail = email.trim();
    const badEmail = !LOGIN_EMAIL_PATTERN.test(cleanEmail);
    const noPassword = !password;
    setEmailInvalid(badEmail);
    setPasswordMissing(noPassword);
    if (badEmail) { emailRef.current?.focus(); return; }
    if (noPassword) { passwordRef.current?.focus(); return; }

    setBusy(true);
    try {
      await onSubmit(cleanEmail, password, remember);
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        setPassword('');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <label className="pl-fld" htmlFor="pl-email">
        Email
        <div className="pl-inp">
          <input
            ref={emailRef}
            id="pl-email"
            name="email"
            type="email"
            autoComplete="username"
            placeholder={LOGIN_EMAIL_PLACEHOLDER}
            value={email}
            onChange={(event) => { setEmail(event.target.value); setEmailInvalid(false); }}
            className={emailInvalid ? 'pl-bad' : ''}
            aria-invalid={emailInvalid}
            aria-describedby={emailInvalid ? 'pl-email-err' : undefined}
            disabled={busy}
          />
        </div>
        {emailInvalid && <span className="pl-err" id="pl-email-err">{LOGIN_EMAIL_ERROR}</span>}
      </label>

      <label className="pl-fld" htmlFor="pl-password">
        <span className="pl-fld-row">
          Password
          {forgotMessage && (
            <button type="button" className="pl-link" onClick={() => setShowForgot((open) => !open)}>
              Forgot password?
            </button>
          )}
        </span>
        <div className="pl-inp pl-pw">
          <input
            ref={passwordRef}
            id="pl-password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => { setPassword(event.target.value); setPasswordMissing(false); }}
            onKeyDown={checkCapsLock}
            onKeyUp={checkCapsLock}
            onBlur={() => setCapsLock(false)}
            className={passwordMissing ? 'pl-bad' : ''}
            aria-invalid={passwordMissing}
            aria-describedby={passwordMissing ? 'pl-password-err' : undefined}
            disabled={busy}
          />
          <button
            type="button"
            className="pl-peek"
            onClick={togglePassword}
            aria-pressed={showPassword}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <PosPasswordPeekIcon shown={showPassword} />
          </button>
        </div>
        {passwordMissing && <span className="pl-err" id="pl-password-err">{LOGIN_PASSWORD_ERROR}</span>}
        {capsLock && <span className="pl-caps">{LOGIN_CAPS_LOCK_WARNING}</span>}
      </label>

      {showRemember && (
        <div className="pl-row">
          <label className="pl-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
              disabled={busy}
            />
            <span className="pl-box"><PosLandingTick strokeWidth={3.2} /></span>
            Keep me signed in
          </label>
        </div>
      )}

      <button type="submit" className="pl-submit" disabled={busy}>
        {busy && <span className="pl-spin" aria-hidden="true" />}
        {busy ? 'Signing in…' : 'Sign in'}
      </button>

      {error && <div className="pl-notice pl-notice-error" role="alert">{error}</div>}
      {showForgot && <div className="pl-notice" role="status">{forgotMessage}</div>}
    </form>
  );
}

export default PosLoginForm;
